import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="grid-scroll"
export default class extends Controller {
  static values = { 
    columnsCount: Number,
    scrollThreshold: { type: Number, default: 10 }
  }

  connect() {
    this.scrollContainer = this.element.querySelector('.table-layout')
    if (!this.scrollContainer) {
      console.warn('Grid scroll controller: .table-layout not found')
      return
    }
    
    this.setupScrollBehavior()
    this.updateScrollIndicators()
    
    // Listen for resize events
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollIndicators()
    })
    this.resizeObserver.observe(this.scrollContainer)
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
  }

  setupScrollBehavior() {
    // Bind scroll event handler
    this.handleScroll = this.handleScroll.bind(this)
    this.scrollContainer.addEventListener('scroll', this.handleScroll)
    
    // Add keyboard navigation
    this.scrollContainer.setAttribute('tabindex', '0')
    this.scrollContainer.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  handleScroll() {
    this.updateScrollIndicators()
  }

  handleKeydown(event) {
    const { key } = event
    const scrollAmount = 200
    
    switch(key) {
      case 'ArrowLeft':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault()
          this.scrollHorizontally(-scrollAmount)
        }
        break
      case 'ArrowRight':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault()
          this.scrollHorizontally(scrollAmount)
        }
        break
      case 'Home':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault()
          this.scrollToStart()
        }
        break
      case 'End':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault()
          this.scrollToEnd()
        }
        break
    }
  }

  scrollHorizontally(amount) {
    this.scrollContainer.scrollBy({
      left: amount,
      behavior: 'smooth'
    })
  }

  scrollToStart() {
    this.scrollContainer.scrollTo({
      left: 0,
      behavior: 'smooth'
    })
  }

  scrollToEnd() {
    this.scrollContainer.scrollTo({
      left: this.scrollContainer.scrollWidth,
      behavior: 'smooth'
    })
  }

  updateScrollIndicators() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer
    const scrollThreshold = this.scrollThresholdValue
    
    // Remove all scroll state classes
    this.scrollContainer.classList.remove('can-scroll-left', 'can-scroll-right', 'scroll-start', 'scroll-end')
    
    const canScrollLeft = scrollLeft > scrollThreshold
    const canScrollRight = (scrollLeft + clientWidth) < (scrollWidth - scrollThreshold)
    const isAtStart = scrollLeft <= scrollThreshold
    const isAtEnd = (scrollLeft + clientWidth) >= (scrollWidth - scrollThreshold)
    
    // Add appropriate classes
    if (canScrollLeft) this.scrollContainer.classList.add('can-scroll-left')
    if (canScrollRight) this.scrollContainer.classList.add('can-scroll-right')
    if (isAtStart) this.scrollContainer.classList.add('scroll-start')
    if (isAtEnd) this.scrollContainer.classList.add('scroll-end')
    
    // Dispatch custom events for external integrations
    this.dispatch('scrollStateChanged', {
      detail: {
        canScrollLeft,
        canScrollRight,
        isAtStart,
        isAtEnd,
        scrollPosition: scrollLeft,
        scrollPercent: scrollWidth > clientWidth ? (scrollLeft / (scrollWidth - clientWidth)) * 100 : 0
      }
    })
  }

  // Public API methods
  scrollToColumn(columnIndex) {
    const table = this.scrollContainer.querySelector('table')
    if (!table) return
    
    const headerCells = table.querySelectorAll('thead th')
    if (headerCells[columnIndex]) {
      const cellRect = headerCells[columnIndex].getBoundingClientRect()
      const containerRect = this.scrollContainer.getBoundingClientRect()
      const scrollOffset = cellRect.left - containerRect.left + this.scrollContainer.scrollLeft - 20 // 20px margin
      
      this.scrollContainer.scrollTo({
        left: scrollOffset,
        behavior: 'smooth'
      })
    }
  }

  // Helper method to check if scroll is needed
  get needsScroll() {
    return this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth
  }

  // Accessibility helpers
  announceScrollState() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollContainer
    const scrollPercent = Math.round((scrollLeft / (scrollWidth - clientWidth)) * 100)
    
    // Create or update live region for screen readers
    let liveRegion = this.element.querySelector('.scroll-live-region')
    if (!liveRegion) {
      liveRegion = document.createElement('div')
      liveRegion.className = 'scroll-live-region sr-only'
      liveRegion.setAttribute('aria-live', 'polite')
      this.element.appendChild(liveRegion)
    }
    
    liveRegion.textContent = `Scrolled ${scrollPercent}% horizontally through table`
  }
}